/**
 * The pilot's Vector Drift record for the profile: solo races finished, wins
 * and podiums, Time Attack runs, time and distance raced, races per craft, and
 * the best lap on each course. Kept on this device (localStorage) like the
 * Time Attack ghosts; online races will record to the ACT account.
 */
import { SHIPS } from "../config/ships";
import { TRACKS } from "../config/tracks";
import { loadRecord } from "../race/records";

export interface LapBest {
  ms: number;
  /** The craft's id (e.g. "vd-01"). */
  ship: string;
}

export interface PilotStats {
  /** Solo races finished: Quick Race and Time Attack. */
  races: number;
  /** Quick Races won, and finished in the top three. */
  wins: number;
  podiums: number;
  /** Time Attack runs finished. */
  timeAttack: number;
  /** Time spent in finished races, ms. */
  raceMs: number;
  /** Distance covered in finished races, metres (the course's length × the laps). */
  distanceM: number;
  /** Finished races per craft id. */
  byShip: Record<string, number>;
  /** The best lap on each course (by id), any solo mode. */
  best: Record<string, LapBest>;
  /** Time Attack records set before this file existed have been read in. */
  seeded: boolean;
}

const KEY = "driftracer.pilot.stats.v1";

const empty = (): PilotStats => ({
  races: 0,
  wins: 0,
  podiums: 0,
  timeAttack: 0,
  raceMs: 0,
  distanceM: 0,
  byShip: {},
  best: {},
  seeded: false,
});

function read(): PilotStats {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { ...empty(), ...(JSON.parse(raw) as Partial<PilotStats>) };
  } catch {
    /* blocked or corrupt storage: start afresh */
  }
  return empty();
}

function write(stats: PilotStats): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(stats));
  } catch {
    /* storage full/blocked — non-fatal */
  }
}

function better(stats: PilotStats, trackId: string, shipId: string, ms: number): boolean {
  const best = stats.best[trackId];
  if (best && best.ms <= ms) return false;
  stats.best[trackId] = { ms, ship: shipId };
  return true;
}

/** The record so far (with any older Time Attack bests read in, once). */
export function loadStats(): PilotStats {
  const stats = read();
  if (!stats.seeded) {
    for (const track of TRACKS) {
      for (const ship of SHIPS) {
        const rec = loadRecord(track.id, ship.id);
        if (rec) better(stats, track.id, ship.id, rec.bestMs);
      }
    }
    stats.seeded = true;
    write(stats);
  }
  return stats;
}

/** A lap completed on one of the game's courses. */
export function recordLap(trackId: string, shipId: string, ms: number): void {
  if (!TRACKS.some((t) => t.id === trackId)) return; // custom courses keep no record
  const stats = loadStats();
  if (better(stats, trackId, shipId, ms)) write(stats);
}

/** A solo race finished on one of the game's courses. `place` is null for Time Attack. */
export function recordRace(race: { trackId: string; shipId: string; place: number | null; raceMs: number; distanceM: number }): void {
  if (!TRACKS.some((t) => t.id === race.trackId)) return;
  const stats = loadStats();
  stats.races += 1;
  if (race.place === null) stats.timeAttack += 1;
  else {
    if (race.place === 1) stats.wins += 1;
    if (race.place <= 3) stats.podiums += 1;
  }
  stats.raceMs += race.raceMs;
  stats.distanceM += race.distanceM;
  stats.byShip[race.shipId] = (stats.byShip[race.shipId] ?? 0) + 1;
  write(stats);
}

/** The craft raced most (its id and race count), or null before the first race. */
export function favouriteShip(stats: PilotStats): { id: string; races: number } | null {
  let top: { id: string; races: number } | null = null;
  for (const [id, races] of Object.entries(stats.byShip)) if (!top || races > top.races) top = { id, races };
  return top;
}
